import type { MappingCorridor } from '@/entities/dashboard/mapping';
import type { LocationModel } from '@/entities/location';

import type { CorridorDisplay } from '../model/types';

/** Legacy's corridor gradient: old journeys fade to grey, fresh ones burn red. */
const CORRIDOR_COLOR_OLD = '#a7a3a3';
const CORRIDOR_COLOR_FRESH = '#c0392b';

/** Legacy draws every corridor with the same stroke (`mapping.js:527`). */
export const CORRIDOR_LINE_WIDTH = 2;
export const CORRIDOR_OPACITY = 0.4;

const mix = (from: string, to: string, ratio: number) => {
  const channel = (offset: number) => {
    const start = parseInt(from.slice(offset, offset + 2), 16);
    const end = parseInt(to.slice(offset, offset + 2), 16);

    return Math.round((end - start) * ratio + start)
      .toString(16)
      .padStart(2, '0');
  };

  return `#${channel(1)}${channel(3)}${channel(5)}`;
};

export function getCorridorColor(daysSinceDispatch: number): string {
  const freshness = Math.max(100 - daysSinceDispatch, 0) / 100;

  return mix(CORRIDOR_COLOR_OLD, CORRIDOR_COLOR_FRESH, freshness);
}

const mercatorY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));

/**
 * Screen-space bearing of the corridor, so the direction marker points the way the
 * line actually runs on a Web Mercator tile rather than the way it runs on a globe.
 */
export function getCorridorAngle(from: LocationModel, to: LocationModel): number {
  return Math.atan2(mercatorY(from.lat) - mercatorY(to.lat), ((to.lng - from.lng) * Math.PI) / 180);
}

/** Legacy puts the direction marker three quarters along the line (`mapping.js:531`). */
export function getCorridorMarkerPosition(from: LocationModel, to: LocationModel): [number, number] {
  return [from.lat + (3 * (to.lat - from.lat)) / 4, from.lng + (3 * (to.lng - from.lng)) / 4];
}

export function createCorridorDisplay(
  corridor: MappingCorridor,
  fromLocation: LocationModel,
  toLocation: LocationModel
): CorridorDisplay {
  return {
    id: `${corridor.from}-${corridor.to}`,
    from: fromLocation,
    to: toLocation,
    consignments: corridor.consignments,
    vehicleCategory: corridor.vehicleCategory,
    vehicleCategoryId: corridor.vehicleCategoryId,
    dispatchDate: corridor.dispatchDate,
    color: getCorridorColor(corridor.daysSinceDispatch),
    angle: getCorridorAngle(fromLocation, toLocation),
    markerPosition: getCorridorMarkerPosition(fromLocation, toLocation)
  };
}

/** Corridors whose ends share a coordinate would render as a dot — legacy skips them. */
export function isCorridorDrawable(from: LocationModel, to: LocationModel): boolean {
  return from.lat !== to.lat || from.lng !== to.lng;
}
